import { Button } from '@/components/ui';
import { cx } from '@/lib/cx';
import { useTrackerSheetLink } from '@/lib/use-tracker-sheet';

/**
 * The "opens somewhere that is not this app" glyph.
 *
 * The same paths `XeroPanel`, `CompanyDetailPage` and `ContactDetailPage`
 * already inline — Lucide's `external-link`. Matched rather than invented: this
 * app carries no icon dependency and declares its glyphs per file, so the thing
 * that keeps them one system is that a new one is copied from an existing one.
 * A second, subtly different arrow would read as a different KIND of link.
 */
export function ExternalTabIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx('shrink-0', className ?? 'h-3.5 w-3.5')}
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/**
 * Opens the tracker's Google Sheets mirror in a new tab.
 *
 * ── IT RENDERS NOTHING UNTIL THERE IS SOMEWHERE TO GO ─────────────────────
 *
 * ⚠️ Absent, never disabled, and never present-then-gone. Three states collapse
 * to one piece of markup here — no mirror configured, the answer not yet known,
 * and the read having failed — and all three mean the same thing to a reader:
 * there is nothing to open. A disabled button would be an affordance whose only
 * outcome is failure, which this codebase already ruled out for the Xero `sync`
 * control; a button that appears on first paint and vanishes a moment later
 * reads as a bug in the app, which is why `loading` is checked and not ignored.
 *
 * ── IT IS A REAL ANCHOR ───────────────────────────────────────────────────
 *
 * This used to be a `<button>` calling `window.open(link, '_blank',
 * 'noopener,noreferrer')`, because `Button` could only render a `<button>`.
 * The security was identical — the features string buys exactly what the `rel`
 * attribute does — but middle-click, "open link in new window" and "copy link
 * address" all belong to anchors, and a control whose entire purpose is opening
 * a URL is the wrong place to give them up. `Button` is polymorphic over `href`
 * now, so this is an `<a>` and gets them back.
 *
 * ⚠️ `rel` IS DELIBERATELY NOT PASSED HERE. `Button` supplies
 * `rel="noopener noreferrer"` for any `target="_blank"` it is given, precisely
 * so a call site cannot forget it — restating it here would suggest the
 * guarantee lives at the call site, and the next such button would be written
 * without it. The sidebar row for this same sheet spells both out because it is
 * hand-written markup rather than a `Button`; the two render the same
 * attributes.
 *
 * ⚠️ There is no `disabled` state to reach for, by construction: `Button`
 * refuses `disabled` on the `href` form at the type level, because HTML has no
 * disabled link. That agrees with what this component already did — the guard
 * above returns `null` rather than showing something inert.
 *
 * ── NO HELPER TEXT ────────────────────────────────────────────────────────
 *
 * The label says what pressing it does and the icon says where it goes. There is
 * deliberately no sentence explaining what the mirror is, when it last ran, or
 * that Google Sheets is a spreadsheet — this app's standing prohibition. The
 * only extra text is `sr-only`, and it is not an explanation: it tells a screen
 * reader the one thing the icon tells everybody else.
 */
export function OpenTrackerSheetButton() {
  const { loading, link } = useTrackerSheetLink();

  if (loading || link === null) return null;

  return (
    <Button
      variant="secondary"
      href={link}
      target="_blank"
      trailingIcon={<ExternalTabIcon />}
    >
      Open in Google Sheets
      <span className="sr-only"> (opens in a new tab)</span>
    </Button>
  );
}
